import QRCode from 'qrcode';
import jsQR from 'jsqr';

export interface MeasuredStream {
  stream: MediaStream;
  canvas: HTMLCanvasElement;
  stop(): void;
}

export async function createMeasuredStream(
  source: MediaStream,
  width: number,
  height: number,
  fps: number,
  clockOffsetMs = 0,
): Promise<MeasuredStream> {
  const video = document.createElement('video');
  video.srcObject = source;
  video.muted = true;
  video.playsInline = true;
  await video.play();
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d', { alpha: false });
  if (!context) throw new Error('Canvas 2D context is unavailable');
  const qrCanvas = document.createElement('canvas');
  let animationFrame = 0;
  let stopped = false;
  let lastQrAt = 0;
  let qrTimestamp = Date.now() + clockOffsetMs;

  const render = (now: number) => {
    if (stopped) return;
    context.drawImage(video, 0, 0, width, height);
    const timestamp = Date.now() + clockOffsetMs;
    if (now - lastQrAt >= 250) {
      qrTimestamp = timestamp;
      lastQrAt = now;
      void QRCode.toCanvas(qrCanvas, JSON.stringify({ t: qrTimestamp }), {
        width: Math.max(96, Math.round(width * 0.14)),
        margin: 1,
        errorCorrectionLevel: 'L',
      });
    }
    const label = new Date(timestamp).toISOString();
    context.font = `600 ${Math.max(18, Math.round(height * 0.035))}px ui-monospace, monospace`;
    const labelWidth = context.measureText(label).width;
    context.fillStyle = 'rgba(0,0,0,.75)';
    context.fillRect(12, 12, labelWidth + 24, Math.max(38, height * 0.06));
    context.fillStyle = '#ffffff';
    context.fillText(label, 24, Math.max(38, height * 0.052));
    context.drawImage(qrCanvas, width - qrCanvas.width - 12, 12);
    animationFrame = requestAnimationFrame(render);
  };
  animationFrame = requestAnimationFrame(render);
  const captured = canvas.captureStream(fps);
  for (const track of source.getAudioTracks()) captured.addTrack(track);
  return {
    stream: captured,
    canvas,
    stop() {
      stopped = true;
      cancelAnimationFrame(animationFrame);
      captured.getVideoTracks().forEach((track) => track.stop());
      video.srcObject = null;
    },
  };
}

export async function estimateClockOffset(samples = 5): Promise<number> {
  const results: Array<{ offset: number; rtt: number }> = [];
  const { api } = await import('./api.js');
  for (let index = 0; index < samples; index += 1) {
    const started = Date.now();
    const server = await api.getTime();
    const ended = Date.now();
    results.push({ rtt: ended - started, offset: server.epochMs - (started + ended) / 2 });
  }
  results.sort((left, right) => left.rtt - right.rtt);
  return results[0]?.offset ?? 0;
}

export function startLatencyDecoder(
  video: HTMLVideoElement,
  onLatency: (latencyMs: number) => void,
  clockOffsetMs = 0,
): () => void {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d', { willReadFrequently: true });
  let stopped = false;
  let timer = 0;
  const decode = () => {
    if (stopped || !context || video.videoWidth === 0) return;
    const width = Math.min(video.videoWidth, 1280);
    const height = Math.round((video.videoHeight / video.videoWidth) * width);
    canvas.width = width;
    canvas.height = height;
    try {
      context.drawImage(video, 0, 0, width, height);
      const image = context.getImageData(0, 0, width, height);
      const decoded = jsQR(image.data, image.width, image.height, { inversionAttempts: 'dontInvert' });
      if (decoded) {
        const payload = JSON.parse(decoded.data) as { t?: number };
        if (typeof payload.t === 'number') onLatency(Math.max(0, Date.now() + clockOffsetMs - payload.t));
      }
    } catch {
      stopped = true;
    }
  };
  timer = window.setInterval(decode, 500);
  return () => {
    stopped = true;
    window.clearInterval(timer);
  };
}

